import * as THREE from 'three'
import { applyGraphParamsToStrands } from './hairStrands.js'
import { enableExtendedSkinning } from './skinInfluences.js'
import { installNodeSkinning } from './skinInfluencesTSL.js'
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js'
import { chooseTextureSource, shouldSwapTexture } from './textureVariant.js'
import { getTextureOverride, beginUpgradeProgress, tickUpgradeProgress, endUpgradeProgress } from './textureQuality.js'
import { applyDefaultTextureWrap } from './textureWrap.js'
import { graphBlendMode } from './graphBlendMode.js'
import { pickBareNameOwners } from './materialSlotOwner.js'
import { slotKey, slotKeyOf, slotLabel } from './materialSlotKey.js'
import {
  classifyMeshBinding, isUnbound, emptyBindingReport, recordBinding,
  bindingSignature, bindingMessages, HIDDEN,
} from './materialBinding.js'
import { unboundMaterial } from './unboundMaterial.js'
import { buildNodeMaterialFromGraph } from './materialGraphTSL.js'

// material_assets-driven material builder. Replaces the legacy role-based
// factories that lived here pre-Phase-7. Every character now renders
// through buildMaterialsFromAssets({ materials, textures, mapping }):
//
//   materials   — material_assets rows from the server
//   textures    — texture_assets rows (UUID → file_url lookup)
//   mapping     — [{ meshIndex, meshName, materialId?, hidden? }, …]
//
// AvatarModel calls applyMaterialMapping(rigScene, built) which walks
// every mesh in the rig and assigns the right Three material by node
// name. EnvProbe reads the flat material list from getAvatarMaterials()._all
// to apply envMap to all of them in one pass.

// ─── Texture cache ─────────────────────────────────────────────────────────

const _texLoader = new THREE.TextureLoader()
const _textureCache = new Map() // (url + sRGB|Linear) → THREE.Texture

// Per-texture provenance for the quality cascade (ENG-757). A WeakMap, NOT
// userData: THREE's Texture.copy/clone treatment of userData is not a contract
// we want to depend on (the KTX2 placeholder path copy()s the loaded texture
// over itself), and a ready-Promise must never go through a JSON round-trip.
//   texture → { rowId, linear, url, edge, ready: Promise }
const _texMeta = new WeakMap()

// Pass-1 load tracking: buildMaterialsFromAssets resets these; AvatarModel
// awaits whenTexturesSettled() and reads getLastBuildTextureUrls() to measure
// real download throughput off the resource-timing entries.
let _pendingLoads = []
let _lastBuildUrls = []

// rowId → max edge currently bound on the avatar (Infinity = full res).
// Drives the content browser's "displayed" column in the Resolutions panel.
const _boundEdgeByRow = new Map()

// KTX2/Basis (ENG-753). Lazily created once we have a renderer so the loader
// can detect which GPU-compressed formats this device supports. Until then
// (and on devices without basis support) we fall back to the original PNG/JPG.
let _ktx2Loader = null

// initKTX2 wires the KTX2 transcoder. Called once by AvatarModel with the R3F
// renderer. Idempotent. Transcoder wasm/js is served locally from /basis/
// (mirrors the local /draco/ decoder), so no CDN dependency.
export function initKTX2(renderer) {
  if (_ktx2Loader || !renderer) return
  _ktx2Loader = new KTX2Loader().setTranscoderPath('/basis/').detectSupport(renderer)
}

/**
 * The stand-in a KTX2 texture occupies until its transcode lands.
 *
 * A VALID EMPTY STATE, not a bare `new CompressedTexture()`. The no-arg
 * constructor leaves `mipmaps` as `undefined` and `image` as
 * `{width: undefined, height: undefined}` — three's own defaults — and the
 * classic WebGLRenderer tolerates that until the real data arrives.
 * `WebGPURenderer` does NOT: `Textures.needsMipmaps` reads
 * `texture.mipmaps.length` unconditionally from `_createBindings`, i.e. whenever
 * the material is BOUND, not just when it uploads.
 *
 * And it does not merely log. The throw lands inside `_renderObjectDirect`,
 * MID-FRAME, leaving three's command encoder unfinished and wedging the renderer
 * PERMANENTLY — every later render silently draws nothing, for every scene, with
 * no further errors, because nothing is rendering left to fail. It presents as
 * "the errors settled down" and a black viewport. Finding it took a control: an
 * identical unlit box rendered 2916 lit pixels through a fresh renderer and 0
 * through the wedged one.
 */
export function ktx2Placeholder() {
  return new THREE.CompressedTexture([], 1, 1)
}

function _loadAssetTexture(url, { linear = false } = {}, loader = _texLoader) {
  if (!url) return null
  const key = url + (linear ? '#L' : '')
  if (_textureCache.has(key)) {
    // Cache hit: no new download (so no throughput sample), but the build's
    // settle barrier still has to cover an in-flight load from a prior call.
    const hit = _textureCache.get(key)
    const ready = _texMeta.get(hit)?.ready
    if (ready) _pendingLoads.push(ready)
    return hit
  }
  // THREE.Cache is shared across modules; clear so we don't pull a copy
  // loaded with the wrong colorSpace earlier in this page's lifecycle.
  THREE.Cache.remove(url)
  const cs = linear ? THREE.NoColorSpace : THREE.SRGBColorSpace

  // KTX2Loader, unlike TextureLoader, returns nothing from load() and delivers
  // the transcoded CompressedTexture via the onLoad callback. Hand back a
  // placeholder immediately and copy the result in when it arrives — the
  // material keeps the same texture instance; needsUpdate re-uploads it.
  if (loader === _ktx2Loader) {
    const tex = ktx2Placeholder()
    tex.colorSpace = cs
    tex.flipY = false
    applyDefaultTextureWrap(tex)
    const ready = new Promise((resolve) => {
      loader.load(url, (loaded) => {
        tex.copy(loaded)
        tex.colorSpace = cs
        tex.flipY = false
        // copy() overwrote wrap from the decoded texture — re-assert the policy.
        applyDefaultTextureWrap(tex)
        tex.needsUpdate = true
        loaded.dispose?.()
        resolve(true)
      }, undefined, (err) => {
        console.warn(`[avatarMaterial] KTX2 decode failed (${url}); leaving placeholder:`, err?.message || err)
        resolve(false)
      })
    })
    _registerLoad(tex, url, ready)
    _textureCache.set(key, tex)
    return tex
  }

  let tex
  const ready = new Promise((resolve) => {
    tex = loader.load(url, () => resolve(true), undefined, (err) => {
      console.warn(`[avatarMaterial] texture load failed (${url}):`, err?.message || err)
      resolve(false)
    })
  })
  tex.colorSpace = cs
  tex.flipY = false
  applyDefaultTextureWrap(tex)
  _registerLoad(tex, url, ready)
  _textureCache.set(key, tex)
  return tex
}

// Track a fresh load for whenTexturesSettled() / throughput measurement.
function _registerLoad(tex, url, ready) {
  _texMeta.set(tex, { ..._texMeta.get(tex), url, ready })
  _pendingLoads.push(ready)
  _lastBuildUrls.push(url)
}

// whenTexturesSettled resolves once every texture requested since the last
// buildMaterialsFromAssets call has loaded or failed. Failures resolve (false)
// rather than reject — the cascade must proceed with whatever arrived.
export function whenTexturesSettled() {
  return Promise.allSettled(_pendingLoads.slice())
}

// getLastBuildTextureUrls — the URLs the last build requested; AvatarModel
// matches these against performance resource-timing entries to measure real
// download throughput for the quality manager.
export function getLastBuildTextureUrls() {
  return _lastBuildUrls.slice()
}

// Load a texture from a texture_assets row, preferring its KTX2 variant at
// the requested quality tier (ENG-757). Loud fallback: if the KTX2 variant
// isn't ready we use the original file; a runtime KTX2 decode failure surfaces
// via three's own console error — never silently swapped for fake data.
function _loadTextureFromRow(row, opts = {}, tier = 'ultra') {
  // A per-texture pin (local to this browser) beats the build/global tier.
  const effTier = getTextureOverride(row.id) ?? tier
  const src = chooseTextureSource(row, !!_ktx2Loader, effTier)
  if (!src) return null
  if (src.ktx2) {
    // A KTX2 variant must never be able to break the material. If the loader
    // throws (transcoder init, unsupported format, decode), fall back to the
    // original PNG/JPG — loudly, so the real reason is visible (ENG-753).
    try {
      const tex = _loadAssetTexture(src.url, opts, _ktx2Loader)
      if (tex) {
        _tagRowMeta(tex, row, opts, src)
        return tex
      }
    } catch (e) {
      console.warn(`[avatarMaterial] KTX2 load failed (${src.url}); using original. Reason:`, e?.message || e)
    }
  }
  if (!row.file_url) return null
  const tex = _loadAssetTexture(row.file_url, opts, _texLoader)
  if (tex) _tagRowMeta(tex, row, opts, { url: row.file_url, edge: Infinity })
  return tex
}

// Record which row/tier a texture instance came from so the upgrade pass can
// re-resolve it at a different tier later.
function _tagRowMeta(tex, row, opts, src) {
  _texMeta.set(tex, {
    ..._texMeta.get(tex),
    rowId: row.id,
    linear: !!opts?.linear,
    url: src.url,
    edge: src.edge,
  })
  _boundEdgeByRow.set(row.id, src.edge)
}

// getTextureResidency — per-rung local/remote state for one texture row, for
// the content browser's Resolutions panel. `downloaded` means the file is in
// this session's texture cache; `boundEdge` is what the avatar is showing.
export function getTextureResidency(row) {
  if (!row) return null
  const inCache = (url) => !!url && (_textureCache.has(url) || _textureCache.has(url + '#L'))
  const rungs = []
  const variants = row.ktx2_variants
  if (variants && typeof variants === 'object') {
    for (const [key, entry] of Object.entries(variants)) {
      const edge = Number(key)
      if (!Number.isFinite(edge)) continue
      rungs.push({
        edge,
        status: entry?.status ?? '',
        byteSize: entry?.byte_size ?? null,
        downloaded: entry?.status === 'done' && inCache(entry.url),
      })
    }
  }
  rungs.sort((a, b) => a.edge - b.edge)
  rungs.push({
    edge: Infinity,
    status: row.ktx2_status === 'done' ? 'done' : (row.ktx2_status || 'original only'),
    byteSize: row.ktx2_byte_size ?? row.byte_size ?? row.byteSize ?? null,
    downloaded: inCache(row.ktx2_url) || inCache(row.file_url),
  })
  return { boundEdge: _boundEdgeByRow.get(row.id) ?? null, rungs }
}

// If d[<slotKey>Offset|Tiling|Rotation] specifies a non-identity transform,
// return a clone of the base texture with that transform applied. Cloning
// preserves the GPU upload (shares the .image / .source) but lets each
// material own its own offset/repeat/rotation matrix.
function _cullToSide(cull) {
  switch (cull) {
    case 0: return THREE.DoubleSide   // none
    case 3: return THREE.DoubleSide   // both
    case 2: return THREE.BackSide     // cull front
    default: return THREE.FrontSide   // 1 = cull back (default)
  }
}

// ─── Live material list (EnvProbe consumes this) ───────────────────────────

let _activeMaterials = []   // flat list of every material currently bound

export function getAvatarMaterials() {
  return { _all: _activeMaterials }
}

// Advance time-driven graph-material uniforms. Called once per frame from
// AvatarModel.useFrame. Mutates the existing uniform objects in place — no
// allocation, no React state (CLAUDE.md hard rule).
export function tickGraphUniforms(elapsedSeconds) {
  for (const m of _activeMaterials) {
    const u = m.userData?.graphUniforms
    if (u?.uTime) u.uTime.value = elapsedSeconds
  }
}

// Live-update one exposed graph param by (assetId, nodeId, handle). Mutates the
// uniform in place — no recompile, no React state. vec3 params accept an array.
// Returns true if the param was found and applied.
export function setGraphParam(assetId, nodeId, handle, value) {
  const mat = _activeMaterials.find(m => m.userData?.assetId === assetId && m.userData?.graphParams)
  if (!mat) return false
  const entry = mat.userData.graphParams[`${nodeId}:${handle}`]
  if (!entry || !entry.uniformObj) return false
  if (Array.isArray(value)) {
    if (entry.uniformObj.value && entry.uniformObj.value.fromArray) entry.uniformObj.value.fromArray(value)
    else entry.uniformObj.value = new THREE.Vector3().fromArray(value)
  } else {
    entry.uniformObj.value = value
  }
  return true
}


// ─── Builders ──────────────────────────────────────────────────────────────

// LegacyMaterialTypeError — a material_assets row whose `type` is not `graph`.
//
// Every material is a node graph (ENG-814). Phase 2b deleted the pbr/toon/skin
// render path, so such a row has NOTHING to render with; the mesh wears the
// neutral placeholder and materialBinding.js reports it as LEGACY_TYPE. That is
// a separate cause from a dangling materialId on purpose: the reference is
// fine, the DATA is behind, and the fix is to run the migration on this
// character rather than to hunt a deleted row.
export class LegacyMaterialTypeError extends Error {
  constructor(materialAsset) {
    super(`material "${materialAsset?.name ?? materialAsset?.id}" has legacy type `
      + `"${materialAsset?.type}" and no render path — run `
      + `POST /api/characters/{slug}:migrateMaterialsToGraph for this character`)
    this.name = 'LegacyMaterialTypeError'
    this.materialId = materialAsset?.id
    this.legacyType = materialAsset?.type
  }
}

// Build one THREE.Material from a material_assets row + a textureById lookup.
// The returned material has userData.assetId set in buildMaterialsFromAssets
// so setGraphParam can find it by row id.
//
// There is no `type` default here any more. It used to be `?? 'pbr'`, which
// made a row with no type render as legacy PBR — the exact silent substitution
// this phase removes. A missing type is a legacy row like any other.
export function buildMaterialFromAsset(materialAsset, textureById, tier = 'ultra') {
  if (materialAsset?.type !== 'graph') throw new LegacyMaterialTypeError(materialAsset)
  return buildGraphMaterial(materialAsset.data ?? {}, textureById, tier)
}

// ONE COMPILER. A material graph is runtime data and it reaches the GPU through
// TSL node trees, because `onBeforeCompile` — the hook a GLSL backend would
// splice through — is never called under `WebGPURenderer`, on either of its
// backends. There is no second arm to choose between: the GLSL compiler and the
// classic renderer that was its only consumer are both gone.
//
// Texture-sample uniforms are resolved here (textureId -> THREE.Texture). On a
// compile error we throw so buildMaterialsFromAssets logs it; the mesh then has
// no built material, so it reports as `unresolved` and wears the neutral
// placeholder — never a silent fake, and never another mesh's look (CLAUDE.md UX
// rule; materialBinding.js).
//
// Everything downstream reads `userData.graphUniforms` / `userData.graphParams`,
// so those are built to the shape those consumers expect: objects with a
// `.value` they write. A TSL `uniform()` and a `TextureNode` both answer to
// exactly that, which is what lets `tickGraphUniforms`, `setGraphParam` and the
// texture tier upgrade work here with no special case.
function buildGraphMaterial(d, textureById, tier = 'ultra') {
  const graph = d.graph ?? { nodes: [], edges: [] }
  const outNode = (graph.nodes ?? []).find(n => n.type === 'PrincipledBSDF')
  const outData = outNode?.data ?? {}
  // There is ONE output node, and ONE place that refuses a graph. A stored row
  // that still carries the removed `ToonBSDF` output is named and explained by
  // the compiler; `buildNodeMaterialFromGraph` throws on any compiler error, so
  // that refusal lands here without this function having to know about it.

  const { mat, compiled, unlitNode } = buildNodeMaterialFromGraph(graph, {
    textureFor: (id, _node, opts) => {
      const row = textureById.get(id)
      return row ? _loadTextureFromRow(row, { linear: !!opts?.linear }, tier) : null
    },
  })

  // Uniforms: the compiler's own nodes, exposed under the names the GLSL path
  // uses. No `uTime` — three's `time` node drives itself on this path, so
  // `tickGraphUniforms` correctly finds nothing to advance.
  const graphUniforms = {}
  for (const [name, u] of Object.entries(compiled.uniforms)) graphUniforms[name] = u.node
  for (const t of compiled.textureNodes) graphUniforms[`tex_${t.nodeId}`] = t.node

  const graphParams = {}
  for (const p of compiled.params ?? []) {
    graphParams[`${p.nodeId}:${p.handle}`] = { ...p, uniformObj: graphUniforms[p.uniform] }
  }
  mat.userData.graphUniforms = graphUniforms
  mat.userData.graphParams = graphParams
  mat.userData.isGraphMaterial = true
  // What the outliner's Meshes ▸ unlit view swaps in. Its PRESENCE is also what
  // tells `applyMeshView` this is a node material, so the two paths need no flag
  // between them — a material with neither an AOS_UNLIT branch nor this (the
  // neutral placeholder, which is unlit already) is left alone by both.
  mat.userData.unlitNode = unlitNode

  const { transparent, alphaTest, alphaToCoverage } = graphBlendMode(outData)
  mat.transparent = transparent
  mat.alphaTest = alphaTest
  mat.alphaToCoverage = alphaToCoverage
  mat.side = outData.twoSided ? THREE.DoubleSide : THREE.FrontSide
  return mat
}

// Build a meshName → THREE.Material map plus a hidden-mesh set, given the
// raw material_assets, texture_assets, and material_mapping arrays straight
// off the API. Caller (AvatarModel) hands the result to applyMaterialMapping.
export function buildMaterialsFromAssets({ materials = [], textures = [], mapping = [], tier = 'ultra' }) {
  // Fresh settle barrier per build (ENG-757): whenTexturesSettled() and the
  // throughput measurement scope to the loads this call kicks off.
  _pendingLoads = []
  _lastBuildUrls = []
  const textureById = new Map(textures.map(t => [t.id, t]))
  const matById = new Map()
  const legacyTypeIds = new Set()
  for (const m of materials) {
    try {
      const built = buildMaterialFromAsset(m, textureById, tier)
      built.userData.assetId = m.id          // for setGraphParam lookup
      matById.set(m.id, built)
    }
    catch (e) {
      // A legacy-typed row is not a broken graph: it has no render path at all
      // since Phase 2b, and the mesh bound to it must say WHICH of the two it
      // is (materialBinding.js LEGACY_TYPE vs UNRESOLVED). Recorded here, then
      // turned into mesh names in the mapping walk below.
      if (e instanceof LegacyMaterialTypeError) legacyTypeIds.add(m.id)
      else console.warn(`[avatarMaterial] failed to build material ${m.name}:`, e)
    }
  }
  const meshNameToMaterial = new Map()
  const hiddenMeshNames = new Set()
  // The three sets applyMaterialMapping classifies against. `mappedMeshNames`
  // is every row whatever its state, so a mesh the mapping never mentions is
  // distinguishable from one it mentions and leaves empty — see
  // materialBinding.js. There is deliberately NO `fallback` here any more: it
  // was always Element 0, and handing it to unbound meshes is the bug this
  // separation exists to make impossible.
  const mappedMeshNames = new Set()
  const unresolvedMeshNames = new Set()
  const legacyTypeMeshNames = new Set()
  // Several rows can share a meshName: the server writes one row per glTF
  // PRIMITIVE, and a MetaHuman face is one mesh with nine. Only ONE of them may
  // speak for the bare node name, and it is the lowest primitiveIndex — three.js
  // gives primitive 0's object the bare name and suffixes the rest. Letting the
  // last row win put alpha-masked cartilage on a face's skin (ENG-1010). See
  // materialSlotOwner.js. Every existing rig has one row per name, so for them
  // the owner is simply that row.
  const owners = pickBareNameOwners(mapping)
  // The SLOT-keyed twins of the name-keyed sets: every row lands here under
  // (meshName, primitiveIndex), which is the key the server wrote and the key a
  // GLTFLoader-stamped object carries (materialSlotKey.js). The name-keyed sets
  // stay for objects with no stamp — the fallback, and every test scene.
  const slotToMaterial = new Map()
  const hiddenSlots = new Set()
  const mappedSlots = new Set()
  const unresolvedSlots = new Set()
  const legacyTypeSlots = new Set()
  for (const entry of mapping) {
    mappedMeshNames.add(entry.meshName)
    const key = slotKey(entry.meshName, entry.primitiveIndex)
    mappedSlots.add(key)
    if (entry.hidden) {
      hiddenSlots.add(key)
    } else if (entry.materialId) {
      const mat = matById.get(entry.materialId)
      if (mat) slotToMaterial.set(key, mat)
      else if (legacyTypeIds.has(entry.materialId)) legacyTypeSlots.add(key)
      else unresolvedSlots.add(key)
    }
    // A non-owner row still marks the name as mapped (above) — the mesh IS in
    // the mapping — but must not decide what the bare-named node wears.
    if (owners.get(entry.meshName) !== entry) continue
    if (entry.hidden) {
      hiddenMeshNames.add(entry.meshName)
      continue
    }
    if (entry.materialId) {
      const mat = matById.get(entry.materialId)
      if (mat) meshNameToMaterial.set(entry.meshName, mat)
      // A row that exists but is legacy-typed is a MIGRATION gap; a materialId
      // naming nothing at all is a DANGLING reference. Different fixes, so they
      // are recorded apart and the console says which it is.
      else if (legacyTypeIds.has(entry.materialId)) legacyTypeMeshNames.add(entry.meshName)
      else unresolvedMeshNames.add(entry.meshName)
    }
  }
  // Refresh the flat list EnvProbe reads. Dedup since multiple meshes
  // legitimately share the same material instance — and a slot-bound material
  // is as active as a name-bound one.
  _activeMaterials = Array.from(new Set([...meshNameToMaterial.values(), ...slotToMaterial.values()]))
  return {
    meshNameToMaterial, hiddenMeshNames, mappedMeshNames, unresolvedMeshNames, legacyTypeMeshNames, matById,
    slotToMaterial, hiddenSlots, mappedSlots, unresolvedSlots, legacyTypeSlots,
  }
}

// ─── Quality-tier upgrade pass (ENG-757) ────────────────────────────────────
//
// Walks every active material's texture slots, re-resolves each bound texture
// against the target tier, and hot-swaps the slot once the replacement has
// loaded. Per-slot swap — no material rebuild, no shader recompile, no flicker.
// Downgrades are refused unless allowDowngrade (the user manually picking a
// lower tier). Runs entirely in async load paths — nothing per-frame.

// The fixed texture-slot list the builders can populate.
const _TEXTURE_SLOTS = [
  'map', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap', 'emissiveMap',
  'thicknessMap', 'clearcoatMap', 'clearcoatRoughnessMap',
  'specularIntensityMap', 'specularColorMap',
]

// upgradeActiveMaterialsTo re-targets all bound textures at `targetTier`.
// textureRows are the texture_assets rows (same array the build used, or a
// re-fetched one). Resolves with the number of slots actually swapped.
export async function upgradeActiveMaterialsTo(targetTier, textureRows, { allowDowngrade = false } = {}) {
  const rowById = new Map((textureRows ?? []).map(r => [r.id, r]))
  const swaps = []

  const planSwap = (currentTex, assign) => {
    const meta = _texMeta.get(currentTex)
    if (!meta || meta.rowId === undefined) return
    const row = rowById.get(meta.rowId)
    if (!row) return
    // A per-texture pin (local to this browser) beats the global target, and
    // reaching a pin is always allowed in both directions — it's explicit.
    const override = getTextureOverride(meta.rowId)
    const rowTier = override ?? targetTier
    const src = chooseTextureSource(row, !!_ktx2Loader, rowTier)
    if (!shouldSwapTexture(meta, src, allowDowngrade || override !== null)) return
    swaps.push(async () => {
      const next = _loadAssetTexture(src.url, { linear: meta.linear }, src.ktx2 ? _ktx2Loader : _texLoader)
      if (!next) return false
      _texMeta.set(next, { ..._texMeta.get(next), rowId: meta.rowId, linear: meta.linear, url: src.url, edge: src.edge })
      const ok = await (_texMeta.get(next)?.ready ?? Promise.resolve(true))
      tickUpgradeProgress()
      if (!ok) {
        console.warn(`[avatarMaterial] tier upgrade load failed (${src.url}); keeping current texture`)
        return false
      }
      // Preserve a UV-transformed clone's transform: the replacement inherits
      // the exact offset/repeat/rotation of what it replaces.
      let bound = next
      const hasUV = currentTex.rotation !== 0 ||
        currentTex.offset.x !== 0 || currentTex.offset.y !== 0 ||
        currentTex.repeat.x !== 1 || currentTex.repeat.y !== 1
      if (hasUV) {
        bound = next.clone()
        _texMeta.set(bound, _texMeta.get(next))
        bound.offset.copy(currentTex.offset)
        bound.repeat.copy(currentTex.repeat)
        bound.rotation = currentTex.rotation
        bound.needsUpdate = true
      }
      assign(bound)
      _boundEdgeByRow.set(meta.rowId, src.edge)
      return true
    })
  }

  for (const mat of _activeMaterials) {
    for (const slot of _TEXTURE_SLOTS) {
      const tex = mat[slot]
      if (tex && tex.isTexture) {
        planSwap(tex, (next) => {
          // Re-check: another upgrade may have swapped this slot while we
          // were loading — last writer wins is fine, stale writer is not.
          if (mat[slot] === tex) {
            mat[slot] = next
            mat.needsUpdate = true
          }
        })
      }
    }
    const uniforms = mat.userData?.graphUniforms
    if (uniforms) {
      for (const u of Object.values(uniforms)) {
        const tex = u?.value
        if (tex && tex.isTexture) {
          planSwap(tex, (next) => {
            if (u.value === tex) u.value = next
          })
        }
      }
    }
  }

  beginUpgradeProgress(swaps.length)
  try {
    const results = await Promise.all(swaps.map(fn => fn()))
    return results.filter(Boolean).length
  } finally {
    endUpgradeProgress()
  }
}

// Walk a rig scene and apply the material map. A mesh the mapping binds nothing
// to wears the NEUTRAL PLACEHOLDER and is reported — never another mesh's
// material. Until 2026-08-31 it inherited `fallback`, "the first mapping entry
// that resolved", which is Element 0 by construction; binding a material to
// Element 0 therefore repainted every unbound mesh on the character. The
// reasoning, and how PlayCanvas handles the same case, is in materialBinding.js.
// Hidden meshes are marked invisible.
//
// Returns the binding report (see emptyBindingReport) so a caller or a spec can
// assert on it; the console lines are emitted here so no caller can forget.
export function applyMaterialMapping(root, built) {
  const { meshNameToMaterial, hiddenMeshNames } = built
  const sets = {
    bound: new Set(meshNameToMaterial.keys()),
    hidden: hiddenMeshNames,
    unresolved: built.unresolvedMeshNames ?? new Set(),
    legacyType: built.legacyTypeMeshNames ?? new Set(),
    mapped: built.mappedMeshNames ?? new Set(meshNameToMaterial.keys()),
  }
  // The same five sets keyed by (meshName, primitiveIndex), for objects that
  // carry a GLTFLoader stamp (materialSlotKey.js). A stamped object whose SLOT
  // the mapping mentions binds by slot; everything else binds by name, as before.
  const slotSets = built.slotToMaterial ? {
    bound: new Set(built.slotToMaterial.keys()),
    hidden: built.hiddenSlots ?? new Set(),
    unresolved: built.unresolvedSlots ?? new Set(),
    legacyType: built.legacyTypeSlots ?? new Set(),
    mapped: built.mappedSlots ?? new Set(),
  } : null
  const report = emptyBindingReport()
  // NO mapping at all is not the same as a mapping that omits a mesh, and the
  // difference decides whether we may touch the scene's materials. The AAM
  // stores an uploaded rig WITHOUT parsing it (materialMappingFromGlb.js), so a
  // character can legitimately have zero rows — there is no binding intent to
  // honour, and the GLB's own embedded materials are the only statement of what
  // it should look like. Repainting the whole character grey there would be a
  // worse silent lie than the fallback this function exists to delete.
  const noMapping = sets.mapped.size === 0
  report.unmappedCharacter = noMapping

  // A bound row lands on a node two ways. A mesh WEARS the compiled graph
  // material. Hair strands (hairStrands.js) keep their own ribbon material and
  // take the row's exposed parameters by name — a MeshPhysicalMaterial has no
  // meaning on a line, and swapping it in would draw the base quad, not hair.
  const bind = (node, state, material) => {
    if (state === HIDDEN) {
      node.visible = false
      return
    }
    if (node.userData.isHairStrands === true) {
      // Unbound strands keep their default look rather than the placeholder: the
      // report still names them, and grey ribbons would read as a broken shader.
      if (!isUnbound(state)) applyGraphParamsToStrands(node.material, material)
      return
    }
    node.material = isUnbound(state) ? unboundMaterial() : material
    // A skinned mesh with more than one joint set needs its material to read
    // them (skinInfluences.js). Per-character materials only: the placeholder
    // is shared, and a define on it would make every other rig's meshes read
    // an attribute they lack.
    if (!isUnbound(state) && node.isSkinnedMesh) {
      const sets = node.geometry?.userData?.skinSets ?? 1
      // Two ways to say the same thing, because the two material systems have
      // nothing in common here: the GLSL path turns on defines over three's
      // patched ShaderChunks, the node path replaces `setupPosition`. Both are
      // called unconditionally and each declines what is not its kind, so a
      // material never has to be asked which it is.
      enableExtendedSkinning(material, sets)
      installNodeSkinning(material, sets, {
        onDowngrade: (why) => console.warn(`[avatarMaterial] ${node.name}: ${why}`),
      })
    }
  }

  root.traverse((node) => {
    const strands = node.userData?.isHairStrands === true
    if (!node.isMesh && !strands) return
    if (strands) {
      // Shadow passes draw the fat-line base quad, not the instances.
      node.castShadow = false
      node.receiveShadow = false
    } else {
      node.castShadow = true
      node.receiveShadow = true
    }
    if (node.isSkinnedMesh) node.frustumCulled = false
    // A garment (garment/garmentAttach.js) is NOT one of this character's
    // meshes. It is attached at runtime, it has no row in material_mapping and
    // cannot have one until a garment asset kind exists server-side, and its
    // own glTF materials are the only statement of what it should look like —
    // the same reasoning the noMapping branch below applies to a whole rig.
    // Binding it anyway did two things, both wrong: it painted all 36 pattern
    // pieces of a Marvelous import with the unbound placeholder (flat grey,
    // measured on aoi-fridaytest), and it filed 36 spurious `unmapped` entries
    // that tell the user their mapping is stale against the rig when it is not.
    // Shadows and culling above still apply: it is in the scene, just not in
    // the mapping.
    if (node.userData?.isGarment === true) return
    if (noMapping) return

    // Join by slot when the object is stamped AND the mapping has that slot;
    // by name otherwise. The report names a slot as `Face[3]` and a name-bound
    // mesh by its name, so a reader can tell which path bound it.
    const key = slotKeyOf(node)
    if (slotSets && key && slotSets.mapped.has(key)) {
      const s = node.userData.materialSlot
      const label = slotLabel(s.meshName, s.primitiveIndex)
      const state = classifyMeshBinding(key, slotSets)
      recordBinding(report, state, label)
      bind(node, state, built.slotToMaterial.get(key))
      return
    }
    const state = classifyMeshBinding(node.name, sets)
    recordBinding(report, state, node.name)
    bind(node, state, meshNameToMaterial.get(node.name))
  })

  reportBinding(report)
  return report
}

// Last report, for console diagnosis and specs. Named for what it is rather
// than for one consumer — the same reason every other window.__* here is.
let _lastBindingSignature = null
function reportBinding(report) {
  if (typeof window !== 'undefined') window.__MATERIAL_BINDING__ = report
  // Two texture-tier passes bind the same scene, and every inspector PATCH
  // calls reloadMaterialsEverywhere, so an unchanged complaint would repeat
  // until it was noise. Log a given set of unbound meshes once.
  const sig = bindingSignature(report)
  if (sig === _lastBindingSignature) return
  _lastBindingSignature = sig
  for (const { level, text } of bindingMessages(report)) {
    (level === 'error' ? console.error : console.warn)(text)
  }
}
